import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { combineLatest, Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { StudyType } from 'src/app/models/study-type';
import { Intervention } from 'src/app/models/intervention';
import { selectInterventions } from 'src/app/dashboard/state/intervention/intervention.selectors';

import * as moment from 'moment';

import { AppState } from 'src/app/state';
import { setSelectedLanguage } from '../../../state/study/study.actions';
import { updateSubscription } from '../../../state/subscription/subscription.actions';
import {
  selectIsLoadingSubscription,
  selectSelectedSubscription,
  selectSubscriptionLoaded,
} from '../../../state/subscription/subscription.selectors';
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages,
} from '../../../state/study/study.selectors';
import {
  selectInformativePages,
  selectSelectedInformativePage,
} from 'src/app/dashboard/state/informative-page/informative-page.selectors';
import { Study } from 'src/app/models/study';
import { Language } from 'src/app/models/language';
import { StudySubscription } from 'src/app/models/subscription';
import { InformativePage } from 'src/app/models/informative-page';
import { environment } from 'src/environments/environment';
import { API_DATE_FORMAT } from '../../../../core/dashboard-constants';
import { STUDY_TITLE_IDENTIFIER } from '../study-details/study-details.component';
import { dateLessThan, removeWhitespaces } from 'src/app/core/custom-form-validators';
import { isThereEmptyTranslation } from '../../helpers/translation-helper';
import { QRCodeModule } from 'angularx-qrcode';
import { element } from 'protractor';

const SUBSCRIPTION_PAGE_TABS = {
  GENERAL: 'general',
  PAGES: 'pages',
  QUESTIONS: 'questions',
};

@Component({
  selector: 'dashboard-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
  encapsulation: ViewEncapsulation.None,
  template: '<qrcode></qrcode>',
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  @ViewChild('subscriptionStartsPicker') studyStartsPicker: any;
  @ViewChild('subscriptionEndsPicker') studyEndsPicker: any;

  subscriptionForm: FormGroup;
  StudyType = StudyType;

  loadingSelectedStudySubscription$: Observable<boolean>;
  selectedStudySubscriptionLoaded$: Observable<boolean>;
  selectedStudySubscription$: Observable<StudySubscription>;
  selectedStudySubscription: StudySubscription;

  @ViewChild('languageTabs', { static: false }) languageTabs: MatTabGroup;
  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;

  informativePages$: Observable<InformativePage[]>;
  informativePages: InformativePage[];

  appUrl = environment.appUrl;
  SUBSCRIPTION_PAGE_TABS = SUBSCRIPTION_PAGE_TABS;

  now = moment(new Date());

  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  tab = 0;
  error_msg_lang = 'Please add a title for each language';
  error_msg_pages = 'Please add at least one page'

  public qrdata: string = '';

  private subscriptions$ = new Subscription();

  interventions$: Observable<Intervention[]>;
  interventions: Intervention[];

  constructor(private formBuilder: FormBuilder, private store: Store<AppState>, private toastrService: ToastrService) {

  }

  ngOnInit(): void {
    this.subscriptionForm = this.formBuilder.group(
      {
        translations: this.formBuilder.array([]),
        startsAt: [this.now, [Validators.required]],
        endsAt: [moment(new Date()).add(1, 'minute'), [Validators.required]],
      },
      { validator: dateLessThan('startsAt', 'endsAt') }
    );

    this.interventions$ = this.store.select(selectInterventions);

    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);

    this.informativePages$ = this.store.select(selectInformativePages);

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions$.add(this.selectedLanguage$.subscribe((language) => (this.selectedLanguage = language)));

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.qrdata = this.selectedStudy?.hash;
    this.subscriptions$.add(this.selectedStudy$.subscribe((study) => (this.selectedStudy = study)));

    this.loadingSelectedStudySubscription$ = this.store.select(selectIsLoadingSubscription);
    this.selectedStudySubscriptionLoaded$ = this.store.select(selectSubscriptionLoaded);
    this.selectedStudySubscription$ = this.store.select(selectSelectedSubscription);

    this.subscriptions$.add(
      combineLatest([this.supportedLanguages$, this.selectedStudySubscription$]).subscribe((data) => {
        this.supportedLanguages = data[0];
        this.selectedStudySubscription = data[1];

        this.removeAllTranslationFields();
        if (this.supportedLanguages?.length) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        } else {
          this.createTranslationFields(this.selectedLanguage.code);
        }
        if (this.selectedStudySubscription?.startsAt) {
          this.startsAt.patchValue(this.selectedStudySubscription.startsAt);
        }
        if (this.selectedStudySubscription?.endsAt) {
          this.endsAt.patchValue(this.selectedStudySubscription.endsAt);
        }
      })
    );

    this.subscriptions$.add(
      this.informativePages$.subscribe((informativePages) => {
        if (informativePages) {
          this.informativePages = informativePages;
        }
      })
    );
  }

  get translations(): FormArray {
    return this.subscriptionForm.get('translations') as FormArray;
  }

  get startsAt(): AbstractControl {
    return this.subscriptionForm.get('startsAt');
  }

  get endsAt(): AbstractControl {
    return this.subscriptionForm.get('endsAt');
  }

  public getStudyHash(study: Study) {
    return study.hash;
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedStudySubscription?.translations?.filter(
      (translation) => translation.language === languageCode
    );
    const subscriptionTitle = this.formBuilder.group({
      id: [filteredTranslations ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [STUDY_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations ? filteredTranslations[0]?.translation : '',
        [Validators.required, Validators.maxLength(100), removeWhitespaces],
      ],
    });
    this.translations.push(subscriptionTitle);
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  buildEnrolmentUrl(): string {
    return this.appUrl + '/enrol/' + this.selectedStudy?.hash;
  }

  onSubscriptionTabChange(event): void {
    this.tab = event?.index;
  }

  saveSubscription(): void {
    if (isThereEmptyTranslation(this.translations.value)) {
      this.toastrService.error('Please populate Title in all selected languages');
      return;
    }

    if (this.subscriptionForm.invalid) {
      return;
    }

    const subscriptionDetails = {
      ...this.subscriptionForm.value,
      startsAt: moment(this.startsAt.value).format(API_DATE_FORMAT),
      endsAt: moment(this.endsAt.value).format(API_DATE_FORMAT),
      studyId: this.selectedStudySubscription.studyId,
    };

    this.store.dispatch(updateSubscription({ subscription: subscriptionDetails }));
  }

  saveAsImage(qrcode) {
    const img = document.getElementById(qrcode).firstChild.firstChild;
    let imageData = this.getBase64Image(img);

    if (window.navigator && window.navigator.msSaveOrOpenBlob) { //IE
      window.navigator.msSaveOrOpenBlob(imageData, 'Qrcode');

    } else { //chrome
      const link = document.createElement('a');
      link.href = imageData;
      link.download = 'Qrcode';
      link.click();
    }
  }


  getBase64Image(img) {
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    var dataURL = canvas.toDataURL("image/jpg");

    return dataURL;
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
